// biome-ignore-all lint/suspicious/noExplicitAny: not changing all these now
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  CreateIdeaViewDto,
  CreateIdeaViewResponseDto,
  GetAllIdeaViewsDto,
  GetIdeaViewByIdResponseDto,
  GetIdeaViewsResponseDto,
  UpdateIdeaViewDto,
  UpdateIdeaViewResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { ideaViewsQueryKeys } from '@/features/intake/queryKeys/ideaViews';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

export const useIdeaViewsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/idea-views`;
  const defaultLimit = 25;

  const createIdeaView = async (view: CreateIdeaViewDto): Promise<CreateIdeaViewResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, view);
    return data;
  };

  const getIdeaView = async (id: number): Promise<GetIdeaViewByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const updateIdeaView = async (
    updateIdea: UpdateIdeaViewDto & { id: number },
  ): Promise<UpdateIdeaViewResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${updateIdea.id}`, {
      ...updateIdea,
      id: undefined,
    });

    return { data };
  };

  const archiveIdeaView = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/archive`);
    return { data };
  };

  const restoreIdeaView = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/restore`);
    return { data };
  };

  const deleteIdeaView = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const updateIdeaViewFields = async (params: {
    id: number;
    fieldIds: number[];
  }): Promise<string> => {
    const { data } = await httpClient.post(
      `${baseUrl}/${params.id}/custom-fields`,
      params.fieldIds,
    );
    return data;
  };

  const getIdeaViews = async (params: GetAllIdeaViewsDto) => {
    const { data } = (await httpClient.get(`${baseUrl}`, { params })) as {
      data: GetIdeaViewsResponseDto;
    };

    const userIds = data.data.map((el) => el.ownerId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: any) => item.ownerId,
    );

    return {
      ...data,
      data: result,
    };
  };

  const useGetAll = (params: GetAllIdeaViewsDto) => {
    const limit = params?.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: ideaViewsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getIdeaViews(newParams);
      },
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const useGetIdeaView = (id: number) => {
    return useQuery({
      queryKey: ideaViewsQueryKeys.detail(id),
      queryFn: () => getIdeaView(id),
      enabled: id > 0,
    });
  };

  const queryClient = useQueryClient();

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createIdeaView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ideaViewsQueryKeys.all() }),
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateIdeaView,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ideaViewsQueryKeys.detail(data.data.id),
        });

        await queryClient.invalidateQueries({ queryKey: ['/ideas'], exact: false });
        return;
      },
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveIdeaView,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ideaViewsQueryKeys.all() }),
          queryClient.resetQueries({ queryKey: ideaViewsQueryKeys.lists() }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreIdeaView,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ideaViewsQueryKeys.all() }),
          queryClient.resetQueries({ queryKey: ideaViewsQueryKeys.lists() }),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteIdeaView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ideaViewsQueryKeys.all() }),
    });

  const useUpdateCustomFields = () =>
    useErrorHandlingMutation({
      mutationFn: updateIdeaViewFields,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ideaViewsQueryKeys.all() }),
    });

  const getIdeaViewActivityLogs = async (id: number) => {
    const { data } = await httpClient.get<ActivityLogDto[]>(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data as Array<{
        id: number;
        displayName?: string;
        creatorName?: string;
        email?: string;
      }>,
      (item) => (item as ActivityLogDto).userId,
    );

    return result as Array<ActivityLogDto & { displayName?: string; creatorName?: string }>;
  };

  const useGetActivityLog = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs'],
      queryFn: () => getIdeaViewActivityLogs(id),
    });

  return {
    useCreate,
    useUpdate,
    useGetIdeaView,
    useGetAll,
    useArchive,
    useRestore,
    useDelete,
    useUpdateCustomFields,
    getIdeaViews,
    useGetActivityLog,
  };
};
